import React from 'react';
import {
  TableContainer,
  Table as MUITable,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  styled,
  tableCellClasses,
  Tooltip,
  Typography,
} from '@mui/material';

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: theme.spacing(1),
  boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
  maxWidth: '100%',
  overflowX: 'auto',
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#e6eaf2',
    color: theme.palette.text.primary,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    padding: '12px 16px',
    fontSize: 14,
  },
  [`&.${tableCellClasses.body}`]: {
    padding: '12px 16px',
    fontSize: 14,
    whiteSpace: 'normal',
    wordBreak: 'break-word',
  },
}));

// Apply width to regular cells too
const WidthTableCell = styled(StyledTableCell, {
  shouldForwardProp: (prop) => !['cellWidth', 'frozen', 'left', 'isHeader'].includes(prop),
})(({ cellWidth }) => ({
  ...(cellWidth && {
    width: cellWidth,
    minWidth: cellWidth,
    maxWidth: cellWidth,
  }),
}));

const FrozenTableCell = styled(StyledTableCell, {
  shouldForwardProp: (prop) => !['frozen', 'left', 'isHeader', 'cellWidth'].includes(prop),
})(({ theme, frozen, left, isHeader, cellWidth }) => {
  const headerBg = '#e6eaf2';
  
  return {
    ...(frozen && {
      position: 'sticky',
      left: left || 0,
      zIndex: isHeader ? 10 : 5,
      overflow: 'hidden',
      ...(cellWidth && {
        width: cellWidth,
        minWidth: cellWidth,
        maxWidth: cellWidth,
        boxSizing: 'border-box',
      }),
      ...(isHeader && {
        backgroundColor: headerBg,
      }),
      [`&.${tableCellClasses.body}`]: {
        overflow: 'hidden',
      },
      '&::after': {
        content: '""',
        position: 'absolute',
        top: 0,
        right: '-1px',
        width: '1px',
        height: '100%',
        backgroundColor: theme.palette.divider,
        opacity: 0.3,
        pointerEvents: 'none',
      },
    }),
  };
});

const ROW_BG_ODD = '#f0f4ff';
const ROW_BG_EVEN = '#f4f6f8';
const ROW_BG_HOVER = '#d6e4ff';
const ROW_BG_SELECTED = '#c5dbff';
const ROW_BG_SELECTED_HOVER = '#b3d1ff';

const StyledTableRow = styled(TableRow, {
  shouldForwardProp: (prop) => !['clickable', 'selected'].includes(prop),
})(({ clickable, selected }) => ({
  backgroundColor: selected ? ROW_BG_SELECTED : undefined,
  '&:nth-of-type(odd)': {
    backgroundColor: selected ? ROW_BG_SELECTED : ROW_BG_ODD,
  },
  '&:nth-of-type(even)': {
    backgroundColor: selected ? ROW_BG_SELECTED : ROW_BG_EVEN,
  },
  '&:hover': {
    boxShadow: '0 4px 8px rgba(0,0,0,0.08)',
  },
  transition: 'background-color 0.15s ease, box-shadow 0.2s ease',
  ...(clickable && {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: selected ? ROW_BG_SELECTED_HOVER : ROW_BG_HOVER,
      boxShadow: '0 4px 8px rgba(0,0,0,0.08)',
      '& .MuiTableCell-root': {
        backgroundColor: selected ? ROW_BG_SELECTED_HOVER : ROW_BG_HOVER,
      },
    },
  }),
  ...(selected && {
    '& .MuiTableCell-root': {
      backgroundColor: ROW_BG_SELECTED,
    },
  }),
}));

const getValue = (row, key) => {
  if (key === 'category' && typeof row[key] === 'string' && row[key].toLowerCase() === 'other') {
    return row['otherCategory'];
  }
  if (key === 'businessModel' && typeof row[key] === 'string' && row[key].toLowerCase() === 'other') {
    return row['otherBusinessModel'];
  }
  return row[key];
};

// Frozen column keys - Logo and Company
const FROZEN_COLUMNS = ['companyLogo', 'companyName'];

const parseWidth = (width) => {
  if (!width) return 100; // Default width if not specified
  if (typeof width === 'number') return width;
  // Extract numeric value from strings like "100px", "150px"
  const match = String(width).match(/(\d+)/);
  return match ? parseInt(match[1], 10) : 100;
};

const Table = ({
  isLoading,
  columns = [],
  data = [],
  rowsPerPage = 10,
  dense = false,
  width = '100%',
  onRowClick,
  selectedRowId,
  getRowId = (row) => row._id,
}) => {
  const clickable = typeof onRowClick === 'function';

  // Calculate left positions for frozen columns
  const getFrozenLeft = (colIndex) => {
    let left = 0;
    for (let i = 0; i < colIndex; i++) {
      if (FROZEN_COLUMNS.includes(columns[i]?.key)) {
        left += parseWidth(columns[i]?.width) || 100;
      }
    }
    return `${left}px`;
  };

  return (
    <StyledTableContainer component={Paper}>
      <MUITable sx={{ minWidth: '100%', maxWidth: 6000, width }}>
        <TableHead>
          <TableRow>
            {columns.map(({ label, width: colWidth, key }, index) => {
              const isFrozen = FROZEN_COLUMNS.includes(key);
              const CellComponent = isFrozen ? FrozenTableCell : WidthTableCell;
              return (
                <CellComponent
                  key={index}
                  variant="head"
                  width={colWidth || 'auto'}
                  cellWidth={colWidth || undefined}
                  frozen={isFrozen}
                  left={isFrozen ? getFrozenLeft(index) : undefined}
                  isHeader={true}
                >
                  {label}
                </CellComponent>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, rowIndex) => {
            const rowId = getRowId(row);
            const isSelected = selectedRowId != null && rowId === selectedRowId;
            const defaultBg = rowIndex % 2 === 0 ? ROW_BG_ODD : ROW_BG_EVEN;

            return (
              <StyledTableRow
                key={rowId ?? rowIndex}
                clickable={clickable}
                selected={isSelected}
                onClick={clickable ? () => onRowClick(row) : undefined}
              >
                {columns.map(({ key, width: colWidth }, colIndex) => {
                  const isFrozen = FROZEN_COLUMNS.includes(key);
                  const CellComponent = isFrozen ? FrozenTableCell : WidthTableCell;

                  return (
                    <CellComponent
                      key={colIndex}
                      width={colWidth || 'auto'}
                      cellWidth={colWidth || undefined}
                      frozen={isFrozen}
                      left={isFrozen ? getFrozenLeft(colIndex) : undefined}
                      isHeader={false}
                      sx={{
                        ...(isFrozen && {
                          backgroundColor: isSelected ? ROW_BG_SELECTED : defaultBg,
                        }),
                      }}
                    >
                      {typeof row[key] === 'string' ? (
                        <Tooltip title={getValue(row, key)} arrow>
                          <Typography
                            sx={{
                              width: '100%',
                              maxWidth: '100%',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              display: 'block',
                            }}
                          >
                            {getValue(row, key)}
                          </Typography>
                        </Tooltip>
                      ) : (
                        row[key]
                      )}
                    </CellComponent>
                  );
                })}
              </StyledTableRow>
            );
          })}
          {!data?.length && (
            <StyledTableRow
              style={{
                height: (dense ? 33 : 53) * rowsPerPage,
              }}
            >
              <StyledTableCell colSpan={columns.length} align='center'>
                {isLoading ? 'Loading...' : 'No data available'}
              </StyledTableCell>
            </StyledTableRow>
          )}
        </TableBody>
      </MUITable>
    </StyledTableContainer>
  );
};

export default Table;
